// src/components/MetaTagsUpdater.js
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://www.kolanut.ai";

// Define metadata for different routes (used for non-blog routes; blog/:id is handled in BlogDetails)
const metaData = {
  "/": {
    title: "See who's slipping. Reach them before they leave. | Kolanut",
    description:
      "Connect your product. Kolanut shows which accounts are at risk, never activated, or gone quiet — then drafts outreach you approve.",
    ogTitle: "See who's slipping. Reach them before they leave.",
    ogDescription:
      "Know which accounts need you. Approve the messages that bring them back.",
    keywords: "SaaS retention, at-risk accounts, churn prevention, customer activation, product engagement, Kolanut",
    ogUrl: `${BASE_URL}/`,
  },
  "/solutions-d2c": {
    title: "Your 24/7 AI Engagement Manager | Kolanut",
    description:
      "Automatically handle personalized outreach, churn prevention, and loyalty campaigns with AI that never sleeps. Built for busy SMBs growing retention without burning more on ads.",
    ogTitle: "Your 24/7 AI Engagement Manager",
    ogDescription:
      "Hire an AI engagement manager. Kolanut handles outreach, churn saves, and loyalty around the clock. No new salary. No sick days.",
    keywords: "AI engagement manager, customer retention, churn prevention, loyalty automation, SMB growth, 24/7 AI",
    ogUrl: `${BASE_URL}/solutions-d2c`,
  },
  "/solutions/paid-ads": {
    title: "Kolanut AI / Customer Growth on Autopilot",
    description: "Kolanut AI is the AI Marketing Co-Pilot for Solo Pros & SMBs — Hyper Personalized Campaigns, Cross Platform Ads Optimization, and Tools and Widgets run automatically while you focus on clients.",
    ogTitle: "Kolanut AI / Customer Growth on Autopilot",
    ogDescription: "Kolanut AI is the AI Marketing Co-Pilot for Solo Pros & SMBs — Hyper Personalized Campaigns, Cross Platform Ads Optimization, and Tools and Widgets run automatically while you focus on clients.",
    keywords: "Kolanut AI, AI marketing co-pilot, solo professionals, SMBs, automated marketing, Hyper Personalized Campaigns, Cross Platform Ads Optimization, Tools and Widgets, AI marketing tool, small business marketing, marketing automation for solopreneurs",
    ogUrl: `${BASE_URL}/solutions/paid-ads`,
  },
  "/client": {
    title: "Kolanut | AI-Powered Holistic Health & Wellness App",
    description: "Kolanut is your AI-powered companion for holistic health. Track nutrition, fitness, and mindfulness, connect with a supportive community, and unlock personalized insights to achieve balanced well-being.",
    ogTitle: "Kolanut – Your AI-Powered Holistic Health Companion",
    ogDescription: "Track nutrition, mindfulness, and fitness with an AI coach. Join a community of wellness seekers and stay motivated on your journey.",
    keywords: "Kolanut, AI health app, holistic health, AI coach, meal tracking, mindfulness, journaling, health community, wellness tracking, well-being",
    ogUrl: `${BASE_URL}/client`,
  },
  "/saas": {
    title: "Kolanut for SaaS | Find Wasted Ad Spend Before Your Next Sprint",
    description: "Connect your product analytics and paid channels. Kolanut scans your last 30 days and shows which campaigns are burning budget without pipeline impact.",
    ogTitle: "Kolanut for SaaS | Find Wasted Ad Spend",
    ogDescription: "Audit paid growth against pipeline outcomes. No auto-execution. Just clear actions your growth team can trust.",
    keywords: "Kolanut SaaS, SaaS marketing audit, wasted ad spend, paid growth, pipeline attribution, growth team tools",
    ogUrl: `${BASE_URL}/saas`,
  },
  "/odoo": {
    title: "Kolanut × Odoo Partnership | AI Growth OS for SMBs",
    description:
      "Partnership overview: Odoo operations plus Kolanut acquisition intelligence. One system from first lead to repeat revenue for modern SMBs.",
    ogTitle: "Kolanut × Odoo Partnership",
    ogDescription: "Building the AI growth operating system for SMBs together.",
    keywords: "Kolanut Odoo partnership, SMB growth stack, acquisition intelligence, Odoo CRM integration",
    ogUrl: `${BASE_URL}/odoo`,
  },
  "/agencies": {
    title: "AI Infrastructure for Brand Uniqueness at Scale | Kolanut For Agencies & Consultants",
    description: "Kolanut empowers enterprise marketing teams and agencies to orchestrate strategy, content, and campaigns across brands — while preserving each brand's creative identity.",
    ogTitle: "AI Infrastructure for Brand Uniqueness at Scale",
    ogDescription: "Kolanut empowers enterprise marketing teams and agencies to orchestrate strategy, content, and campaigns across brands — while preserving each brand's creative identity.",
    keywords: "Kolanut for agencies & consultants, AI marketing infrastructure, brand management, enterprise marketing automation, agency tools, multi-brand marketing, creative identity, marketing orchestration, enterprise AI tools",
    ogUrl: `${BASE_URL}/agencies`,
  },
  "/about": {
    title: "About Us | Kolanut AI",
    description: "Learn about Kolanut AI — the AI marketing co-pilot helping solo pros and SMBs grow with hyper personalized campaigns, cross-platform ads optimization, and powerful tools.",
    ogTitle: "About Us | Kolanut AI",
    ogDescription: "Learn about Kolanut AI and how we help businesses automate marketing and grow on autopilot.",
    keywords: "Kolanut about, AI marketing, company, team, mission",
    ogUrl: `${BASE_URL}/about`,
  },
  "/blog": {
    title: "Blog | Kolanut AI – Marketing Tips & Insights",
    description: "Marketing tips, growth strategies, and product updates from Kolanut AI. Learn how to automate campaigns, optimize ads, and grow your business.",
    ogTitle: "Blog | Kolanut AI",
    ogDescription: "Marketing tips, growth strategies, and insights from Kolanut AI.",
    keywords: "Kolanut blog, marketing tips, growth strategies, AI marketing insights",
    ogUrl: `${BASE_URL}/blog`,
  },
  "/pricing": {
    title: "Pricing | Kolanut AI",
    description: "Kolanut AI pricing plans for solo professionals and small businesses. Start your free trial and grow on autopilot.",
    ogTitle: "Pricing | Kolanut AI",
    ogDescription: "Simple, transparent pricing for the AI marketing co-pilot. Start free.",
    keywords: "Kolanut pricing, plans, free trial, subscription",
    ogUrl: `${BASE_URL}/pricing`,
  },
  "/book-demo": {
    title: "Book a Demo | Kolanut",
    description:
      "Watch a short Kolanut walkthrough and book a live demo. See how AI engagement works for your SaaS team.",
    ogTitle: "Book a Demo | Kolanut",
    ogDescription:
      "Watch the demo, then pick a time. Real product walkthrough — no pitch deck.",
    keywords: "Kolanut demo, book demo, product walkthrough, SaaS engagement demo",
    ogUrl: `${BASE_URL}/book-demo`,
  },
  "/healing": {
    title: "Kolanut Heal | Customer feedback in, tested fixes out",
    description:
      "An npm package that turns customer feedback into tested pull requests. An agent reproduces the bug in a private sandbox, fixes it, and ships live before/after previews.",
    ogTitle: "Your customers report the bug. Your codebase heals itself.",
    ogDescription:
      "npm install @kolanut/heal: feedback becomes tested fixes with live before/after previews.",
    keywords: "self-healing software, AI bug fixing, agentic coding, customer feedback, automated pull requests, Kolanut Heal",
    ogUrl: `${BASE_URL}/healing`,
  },
  "/kolanut-thesis": {
    title: "What we believe | Kolanut",
    description:
      "Software has always been able to serve millions of customers. Until now, it could not know millions of customers. Kolanut is built on a simple belief: AI changes that.",
    ogTitle: "What we believe | Kolanut",
    ogDescription:
      "One intelligent relationship for every customer.",
    keywords: "Kolanut thesis, AI customer relationships, product usage, customer success",
    ogUrl: `${BASE_URL}/kolanut-thesis`,
  },
  "/ng": {
    title: "Kolanut.ai for Nigerian Merchants | Turn POS Data Into Repeat Revenue",
    description: "Connect Moniepoint, OPay, PalmPay, and Nomba in under 60 seconds. Kolanut.ai turns transaction data into hyper-personalized WhatsApp flows, SMS nudges, and ad campaigns that drive repeat sales.",
    ogTitle: "Kolanut.ai for Nigerian Merchants",
    ogDescription: "Turn your POS data into repeat revenue with AI-powered WhatsApp, SMS, and ad campaigns built for Nigerian merchants.",
    keywords: "Kolanut Nigeria, Nigerian merchants, POS marketing, Moniepoint, OPay, PalmPay, Nomba, WhatsApp marketing, SMS marketing, repeat sales",
    ogUrl: `${BASE_URL}/ng`,
  },
  "/privacy-policy": {
    title: "Privacy Policy | Kolanut",
    description: "Learn about how Kolanut collects, uses, and protects your personal information.",
    ogTitle: "Privacy Policy | Kolanut",
    ogDescription: "Learn more about how Kolanut collects, uses, and protects your personal information.",
    keywords: "privacy policy, data protection, user privacy, Kolanut privacy",
    ogUrl: `${BASE_URL}/privacy-policy`,
  },
  "/terms-of-use": {
    title: "Terms of Use | Kolanut",
    description: "Terms of use and service for Kolanut AI. Read our terms and conditions.",
    ogTitle: "Terms of Use | Kolanut",
    ogDescription: "Terms of use and service for Kolanut AI.",
    keywords: "terms of use, terms and conditions, Kolanut",
    ogUrl: `${BASE_URL}/terms-of-use`,
  },
  "/ht_phyt": {
    title: "Coach HT | The PHYT Gym – Nutrition & Fitness Coach",
    description: "Meet Coach HT, founder of The PHYT Gym. Specializing in nutrition, fitness, and women's health. Book a consultation and join the PHYT community.",
    ogTitle: "Coach HT – Nutrition & Fitness Coach | The PHYT Gym",
    ogDescription: "Transform your health with Coach HT. Expert in nutrition, fitness, and pre/post-natal training. Discover programs, testimonials, and more.",
    keywords: "Coach HT, PHYT Gym, nutrition coach, fitness, women's health, pre-natal, post-natal, Los Angeles, personal trainer",
    ogUrl: `${BASE_URL}/ht_phyt`,
    ogImage: `${BASE_URL}/coach_ht.png`,
    themeColor: "#7c3aed",
  },
  "/use-case/small-business": {
    title: "AI Marketing for Small Business | Kolanut",
    description: "How small businesses use Kolanut AI to automate marketing, optimize ad spend, and grow without a big team.",
    ogTitle: "Small Business Marketing | Kolanut AI",
    ogDescription: "AI marketing automation built for small businesses.",
    keywords: "small business marketing, SMB, AI marketing, Kolanut",
    ogUrl: `${BASE_URL}/use-case/small-business`,
  },
  "/use-case/solo-pro": {
    title: "AI Marketing for Solo Professionals | Kolanut",
    description: "Solo pros use Kolanut to run hyper personalized campaigns, ads, and widgets on autopilot while focusing on clients.",
    ogTitle: "Solo Pro Marketing | Kolanut AI",
    ogDescription: "Marketing on autopilot for consultants, coaches, and solo service pros.",
    keywords: "solo professional, solopreneur, AI marketing, Kolanut",
    ogUrl: `${BASE_URL}/use-case/solo-pro`,
  },
  "/features/referral": {
    title: "Referral Agent | Kolanut AI",
    description: "Grow your customer base with Kolanut's referral program tools and AI-powered referral campaigns.",
    ogTitle: "Referral Agent | Kolanut AI",
    ogDescription: "Referral program and campaigns on autopilot.",
    keywords: "referral program, referral marketing, Kolanut",
    ogUrl: `${BASE_URL}/features/referral`,
  },
  "/features/customer-happiness": {
    title: "Customer Happiness Agent | Kolanut AI",
    description: "Keep customers happy with AI-powered support and engagement. Kolanut's Customer Happiness Agent works 24/7.",
    ogTitle: "Customer Happiness Agent | Kolanut AI",
    ogDescription: "AI-powered customer support and happiness.",
    keywords: "customer happiness, support agent, AI support, Kolanut",
    ogUrl: `${BASE_URL}/features/customer-happiness`,
  },
  "/features/visibility-engagement": {
    title: "Visibility & Engagement Agent | Kolanut AI",
    description: "Boost visibility and engagement with Kolanut's AI. Content, social, and campaigns run automatically.",
    ogTitle: "Visibility & Engagement Agent | Kolanut AI",
    ogDescription: "AI-driven visibility and engagement for your brand.",
    keywords: "visibility, engagement, content marketing, Kolanut",
    ogUrl: `${BASE_URL}/features/visibility-engagement`,
  },
  "/features/predictable-growth": {
    title: "Predictable Growth Agent | Kolanut AI",
    description: "Achieve predictable growth with Kolanut's AI. Strategy, campaigns, and optimization in one place.",
    ogTitle: "Predictable Growth Agent | Kolanut AI",
    ogDescription: "Predictable growth through AI marketing automation.",
    keywords: "predictable growth, growth marketing, Kolanut",
    ogUrl: `${BASE_URL}/features/predictable-growth`,
  },
  "/features/customer-insight": {
    title: "Customer Insight Agent | Kolanut AI",
    description: "Understand your customers with Kolanut's AI. Insights, segments, and personalized campaigns.",
    ogTitle: "Customer Insight Agent | Kolanut AI",
    ogDescription: "AI-powered customer insights and segmentation.",
    keywords: "customer insight, analytics, segmentation, Kolanut",
    ogUrl: `${BASE_URL}/features/customer-insight`,
  },
  "/features/auto-branded-assets": {
    title: "Auto-Branded Assets | Kolanut AI",
    description: "Generate on-brand creative and assets automatically. Kolanut keeps your look consistent across all channels.",
    ogTitle: "Auto-Branded Assets | Kolanut AI",
    ogDescription: "AI-generated branded assets and creative.",
    keywords: "branded assets, creative automation, Kolanut",
    ogUrl: `${BASE_URL}/features/auto-branded-assets`,
  },
  "/all-in-one-coaching": {
    title: "All-in-One Coaching | Kolanut",
    description: "Kolanut's all-in-one coaching platform. Tools, campaigns, and growth in one place.",
    ogTitle: "All-in-One Coaching | Kolanut",
    ogDescription: "All-in-one coaching and marketing platform.",
    keywords: "coaching platform, all-in-one, Kolanut",
    ogUrl: `${BASE_URL}/all-in-one-coaching`,
  },
  "/gifts": {
    title: "Free Gifts & Resources | Kolanut AI",
    description: "Free quizzes, ebooks, templates, and webinars to grow your business. From Kolanut AI.",
    ogTitle: "Free Gifts & Resources | Kolanut AI",
    ogDescription: "Free marketing resources and lead magnets.",
    keywords: "free gifts, lead magnets, resources, Kolanut",
    ogUrl: `${BASE_URL}/gifts`,
  },
  "/gifts/free-quiz": {
    title: "Free Quiz | Kolanut AI",
    description: "Discover the hidden gaps holding your business back. Free assessment from Kolanut.",
    ogTitle: "Free Business Quiz | Kolanut AI",
    ogDescription: "Free assessment and quiz for your business.",
    keywords: "free quiz, assessment, Kolanut",
    ogUrl: `${BASE_URL}/gifts/free-quiz`,
  },
  "/gifts/ebook": {
    title: "Free Ebook | Kolanut AI",
    description: "The AI Revolution: How smart coaches book more clients with half the effort. Free ebook.",
    ogTitle: "Free Ebook | Kolanut AI",
    ogDescription: "Free ebook on AI and coaching growth.",
    keywords: "free ebook, lead magnet, Kolanut",
    ogUrl: `${BASE_URL}/gifts/ebook`,
  },
  "/gifts/funnel-template": {
    title: "Funnel Template | Kolanut AI",
    description: "Copy & paste this proven funnel for wellness coaches. Free template from Kolanut.",
    ogTitle: "Funnel Template | Kolanut AI",
    ogDescription: "Proven funnel template for coaches.",
    keywords: "funnel template, lead magnet, Kolanut",
    ogUrl: `${BASE_URL}/gifts/funnel-template`,
  },
  "/gifts/webinar": {
    title: "Free Webinar | Kolanut AI",
    description: "Double your revenue without burnout. Join our free webinar.",
    ogTitle: "Free Webinar | Kolanut AI",
    ogDescription: "Free webinar on growth and revenue.",
    keywords: "webinar, free webinar, Kolanut",
    ogUrl: `${BASE_URL}/gifts/webinar`,
  },
  "/fliers-home": {
    title: "Fliers | Kolanut AI",
    description: "Kolanut Fliers — flyers and local marketing on autopilot.",
    ogTitle: "Fliers | Kolanut AI",
    ogDescription: "Flyers and local marketing automation.",
    keywords: "fliers, flyers, local marketing, Kolanut",
    ogUrl: `${BASE_URL}/fliers-home`,
  },
  "/medspa": {
    title: "Med Spa Marketing | Kolanut AI",
    description: "AI marketing for med spas. Campaigns, reviews, and growth on autopilot.",
    ogTitle: "Med Spa Marketing | Kolanut AI",
    ogDescription: "Marketing automation for med spas.",
    keywords: "med spa marketing, spa marketing, Kolanut",
    ogUrl: `${BASE_URL}/medspa`,
  },
  "/requests": {
    title: "Requests | Kolanut AI",
    description: "Submit a request or get in touch with Kolanut AI.",
    ogTitle: "Requests | Kolanut AI",
    ogDescription: "Contact and requests for Kolanut AI.",
    keywords: "contact, requests, Kolanut",
    ogUrl: `${BASE_URL}/requests`,
  },
  "/shopify": {
    title: "Kolanut AI — Growth Copilot for Shopify | Product Spec",
    description: "A no-BS intelligence layer that turns your Shopify order history into decisions — not dashboards. Seasonal intelligence, product profit radar, segments, bundles, and more.",
    ogTitle: "Kolanut AI — Growth Copilot for Shopify",
    ogDescription: "Intelligence layer for Shopify: seasonal insights, product margin, segments, bundles, geo opportunity, and NL Q&A over your store data.",
    keywords: "Kolanut Shopify, Shopify analytics, Shopify AI, growth copilot, merchant intelligence, store analytics",
    ogUrl: `${BASE_URL}/shopify`,
  },
};

// Get meta for path; blog post routes use default until BlogDetails sets them
function getMetaForPath(path) {
  return metaData[path] || metaData["/"];
}

function MetaTagsUpdater() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;

    // Blog post pages are handled in BlogDetails (dynamic meta from API)
    const isBlogPost = /^\/blog\/[^/]+$/.test(path);
    const currentMeta = isBlogPost ? metaData["/blog"] : getMetaForPath(path);

    const canonicalUrl = path === "/" ? BASE_URL : `${BASE_URL}${path}`;

    document.title = currentMeta.title;

    const metaTags = {
      description: document.querySelector('meta[name="description"]'),
      keywords: document.querySelector('meta[name="keywords"]'),
      ogTitle: document.querySelector('meta[property="og:title"]'),
      ogDescription: document.querySelector('meta[property="og:description"]'),
      ogUrl: document.querySelector('meta[property="og:url"]'),
      twitterTitle: document.querySelector('meta[name="twitter:title"]'),
      twitterDescription: document.querySelector('meta[name="twitter:description"]'),
      ogImage: document.querySelector('meta[property="og:image"]'),
      themeColor: document.querySelector('meta[name="theme-color"]'),
    };

    if (metaTags.description) metaTags.description.setAttribute("content", currentMeta.description);
    if (metaTags.keywords) metaTags.keywords.setAttribute("content", currentMeta.keywords);
    if (metaTags.ogTitle) metaTags.ogTitle.setAttribute("content", currentMeta.ogTitle);
    if (metaTags.ogDescription) metaTags.ogDescription.setAttribute("content", currentMeta.ogDescription);
    if (metaTags.ogUrl) metaTags.ogUrl.setAttribute("content", currentMeta.ogUrl);
    if (metaTags.twitterTitle) metaTags.twitterTitle.setAttribute("content", currentMeta.ogTitle);
    if (metaTags.twitterDescription) metaTags.twitterDescription.setAttribute("content", currentMeta.ogDescription);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // /ht_phyt: custom og:image and theme-color
    if (path === "/ht_phyt") {
      if (metaTags.ogImage) {
        metaTags.ogImage.setAttribute("content", currentMeta.ogImage);
      } else {
        const ogImg = document.createElement("meta");
        ogImg.setAttribute("property", "og:image");
        ogImg.setAttribute("content", currentMeta.ogImage);
        document.head.appendChild(ogImg);
      }
      if (metaTags.themeColor) {
        metaTags.themeColor.setAttribute("content", currentMeta.themeColor);
      } else {
        const theme = document.createElement("meta");
        theme.setAttribute("name", "theme-color");
        theme.setAttribute("content", currentMeta.themeColor);
        document.head.appendChild(theme);
      }
    }
  }, [location.pathname]);

  return null;
}

export default MetaTagsUpdater;
export { BASE_URL, getMetaForPath };
